import React from 'react';
import { RegistrationItem } from '../../../models/registrations/registrations';
import { formatCurrencyValue } from '../../../utils/currency';
import { LineItem } from '../../LineItems/LineItem';
import { ForSaleConfiguration, LineItemTypes } from '../../LineItems/LineItemTypes/LineItemTypes';
import { fixedPriceConfiguration, LineItemUnitPriceTypes } from '../../LineItems/UnitPriceTypes/LineItemUnitPriceTypes';

interface StandardItemPanelProps {
    registrationItem: RegistrationItem,
    currentTime: moment.Moment,
    adjustGrandTotal: (arg0: number) => void,
    adjustCartItem: (id: number, cart: LineItem) => void,
    disableInputs: boolean
}


const StandardItemPanel = (props: StandardItemPanelProps) => {

    const [quantityOrdered, setQuantityOrdered] = React.useState<number>(0);

    const currency = {
        prefix: '$',
        suffix: '',
        decimalSeparator: '.',
        groupSeparator: ',',
        decimalLimit: 2
    }

    const unitPrice = props.registrationItem.unitPrice ? formatCurrencyValue(props.registrationItem.unitPrice, currency) : undefined;

    const unitPriceFormatted = unitPrice ? unitPrice.formatted : undefined;

    const isInventoryInfinite = props.registrationItem.remainingInventory === null;

    const isInventoryAvailable = props.registrationItem.remainingInventory != 0;



    React.useEffect(() => 
    {
        const unitPriceValue = {
            currency: {
                name: 'USD',
                localization: Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }),
                formatting: {
                    prefix: '$',
                    suffix: '',
                    decimalSeparator: '.',
                    groupSeparator: ',',
                    decimalLimit: 2
                }
            },
            value: unitPrice
        }
        const cartItem: LineItem = 
        {
            itemFormat: LineItemTypes.ForSale,
            itemConfiguration: 
            {
                dbId: props.registrationItem.id,
                componentName:'LineItemData',
                quantity: quantityOrdered,
                unitPrice: unitPriceValue,
                maximumPermittedInventory: props.registrationItem.remainingInventory
            } as ForSaleConfiguration,
            name: props.registrationItem.displayName,
            displayOrd: props.registrationItem.displayOrd ?? 0,
            unitPriceFormat: LineItemUnitPriceTypes.FixedPrice,
            defaultPriceDisplay: unitPriceFormatted,
            unitPriceConfiguration: 
            {
                    componentName: 'LineItemData',
                    defaultPrice: {
                        currency: {
                            name: 'USD',
                            localization: Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                            }),
                            formatting: {
                                prefix: '$',
                                suffix: '',
                                decimalSeparator: '.',
                                groupSeparator: ',',
                                decimalLimit: 2
                            }
                        },
                        value: props.registrationItem.unitPrice ? formatCurrencyValue(props.registrationItem.unitPrice * quantityOrdered, currency) : 0
                    },
                    price: {
                        currency: {
                            name: 'USD',
                            localization: Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                            }),
                            formatting: {
                                prefix: '$',
                                suffix: '',
                                decimalSeparator: '.',
                                groupSeparator: ',',
                                decimalLimit: 2
                            }
                        },
                        value: unitPrice
                    }
            } as fixedPriceConfiguration
        };

        props.adjustCartItem(props.registrationItem.id, cartItem);
    }, [quantityOrdered]); 
    


    React.useEffect(() => 
    {
        const cartItem: LineItem = 
        {
            itemFormat: LineItemTypes.ForSale,
            itemConfiguration: 
            {
                dbId: props.registrationItem.id,
                componentName:'LineItemData',
                quantity: 0,
                unitPrice: unitPrice,
                maximumPermittedInventory: props.registrationItem.remainingInventory
            } as ForSaleConfiguration,
            name: props.registrationItem.displayName,
            displayOrd: props.registrationItem.displayOrd ?? 0,
            unitPriceFormat: LineItemUnitPriceTypes.FixedPrice,
            defaultPriceDisplay: unitPriceFormatted,
            unitPriceConfiguration: 
            {
                    componentName: 'LineItemData',
                    defaultPrice: {
                        currency: {
                            name: 'USD',
                            localization: Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                            }),
                            formatting: {
                                prefix: '$',
                                suffix: '',
                                decimalSeparator: '.',
                                groupSeparator: ',',
                                decimalLimit: 2
                            }
                        },
                        value: 0
                    },
                    price: {
                        currency: {
                            name: 'USD',
                            localization: Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                            }),
                            formatting: {
                                prefix: '$',
                                suffix: '',
                                decimalSeparator: '.',
                                groupSeparator: ',',
                                decimalLimit: 2
                            }
                        },
                        value: unitPrice
                    }
            } as fixedPriceConfiguration
        };

        props.adjustCartItem(props.registrationItem.id, cartItem);
    }, []);  



    const isOrderingEnabled = () => {
        if (props.disableInputs)
        {
            return {
                incrementerEnabled: false,
                decrementerEnabled: false
            }
        }
        if (isInventoryInfinite)
        {
            return {
                incrementerEnabled: true,
                decrementerEnabled: true
            }
        }
        if (isInventoryAvailable && props.registrationItem.remainingInventory)  
        {
            return {
                incrementerEnabled: quantityOrdered < props.registrationItem.remainingInventory,
                decrementerEnabled: quantityOrdered > 0
            }
        } 
        else 
        {
            return {
                incrementerEnabled: false,
                decrementerEnabled: false
            }
        }
    }



    const inventoryDisplay = () =>
    {
        if (isInventoryInfinite)
        {
            return <div></div>
        }
        if (isInventoryAvailable)
        {
            return <i> {props.registrationItem.remainingInventory} remaining</i>
        }
        return <i>Sold out</i>
    }



    return(
        <div className='colPanel-lineItem'>
            <div className='leftAlign'>
                <b>{props.registrationItem.displayName}</b>
            </div>
            <div className='colPanel-buttonGroup'>

                <button
                    className='colPanel-button'
                    disabled={!isOrderingEnabled().decrementerEnabled}
                    onClick={() => {
                        setQuantityOrdered(quantityOrdered-1);
                        props.registrationItem.unitPrice && props.adjustGrandTotal(props.registrationItem.unitPrice * -1);
                    }}>-</button>
                <b className='colPanel-inline'>{quantityOrdered}</b>
                <button
                    className='colPanel-button'
                    disabled={!isOrderingEnabled().incrementerEnabled}
                    onClick={() => {
                        setQuantityOrdered(quantityOrdered+1);
                        props.registrationItem.unitPrice && props.adjustGrandTotal(props.registrationItem.unitPrice);
                    }}>+</button>

            </div>
            <div className='leftAlign'>
                <span>{unitPriceFormatted}</span>
            </div>
            <div className='rightAlign'>
                {inventoryDisplay()}
                <br></br>
            </div>
        </div>
    )
}

export default StandardItemPanel;